import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, downloadDeliveredPdf } from '../api';

type DeliveredItem = {
  id: string;
  version: string;
  delivered_at: string | null;
  demo_mode: boolean;
  first_name: string | null;
  email: string | null;
  industry: string | null;
  path: string | null;
  qualifier_choice: string | null;
  register: string | null;
  maturity_score: number | null;
  top_priority: string | null;
  pdf_url: string | null;
  pdf_error: string | null;
};

export default function Delivered() {
  const [search, setSearch] = useSearchParams();
  const version = search.get('version') || 'all';
  const industry = search.get('industry') || '';
  const path = search.get('path') || '';

  const [items, setItems] = useState<DeliveredItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null); setError(null);
    (async () => {
      try {
        const r = await api.delivered({ version, industry: industry || undefined, path: path || undefined });
        if (!cancelled) setItems(r.items);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Could not load.');
      }
    })();
    return () => { cancelled = true; };
  }, [version, industry, path]);

  async function handleDownload(item: DeliveredItem) {
    if (!item.pdf_url) return;
    setBusyId(item.id);
    setError(null);
    try {
      const filename = item.pdf_url.split('/').pop() || undefined;
      await downloadDeliveredPdf(item.id, filename);
    } catch (err: any) {
      setError(err?.message || 'Download failed.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleRerender(item: DeliveredItem) {
    if (!confirm('Re-render this PDF? This will retry against R2 and update tempo_outputs.')) return;
    setBusyId(item.id);
    setError(null);
    try {
      await api.rerenderPdf(item.id);
      // Refresh the list so the row reflects the new pdf_url / cleared error.
      const r = await api.delivered({ version, industry: industry || undefined, path: path || undefined });
      setItems(r.items);
    } catch (err: any) {
      setError(err?.message || 'Re-render failed.');
    } finally {
      setBusyId(null);
    }
  }

  function update(key: string, value: string) {
    const next = new URLSearchParams(search);
    if (value && value !== 'all') next.set(key, value); else next.delete(key);
    setSearch(next, { replace: true });
  }

  return (
    <div className="max-w-6xl">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="font-display text-3xl font-medium text-ink">Delivered</h1>
        {items && <span className="font-mono text-[11px] uppercase tracking-widest text-ink-light">{items.length} item{items.length === 1 ? '' : 's'}</span>}
      </div>

      <div className="flex flex-wrap gap-3 mb-6 pb-4 border-b border-rule">
        <Sel label="Version" value={version} options={[{ value: 'all', label: 'All' }, { value: 'v1', label: 'v1 (legacy)' }, { value: 'v2', label: 'v2' }]} onChange={(v) => update('version', v)} />
        <Sel label="Industry" value={industry} options={[
          { value: '', label: 'All industries' },
          { value: 'professional_services', label: 'Professional services' },
          { value: 'real_estate', label: 'Real estate' },
          { value: 'health_wellness', label: 'Health & wellness' },
          { value: 'ecommerce_retail', label: 'E-commerce' },
          { value: 'trades_manufacturing', label: 'Trades' },
          { value: 'saas_software', label: 'SaaS' },
          { value: 'other', label: 'Other' },
        ]} onChange={(v) => update('industry', v)} />
        <Sel label="Path" value={path} options={[
          { value: '', label: 'All paths' },
          { value: 'pathA', label: 'Founder-Led' },
          { value: 'pathB', label: 'Management-Led' },
        ]} onChange={(v) => update('path', v)} />
      </div>

      {error && <p className="font-serif text-accent">{error}</p>}
      {!items && !error && <p className="font-mono text-[11px] uppercase tracking-wider text-ink-light">Loading…</p>}
      {items && items.length === 0 && <p className="font-serif text-ink-light italic">No delivered diagnostics in this view.</p>}
      {items && items.length > 0 && (
        <div className="overflow-x-auto border border-rule bg-paper">
          <table className="w-full font-serif text-sm">
            <thead>
              <tr className="bg-cream-dark text-left">
                <Th>Delivered</Th>
                <Th>Operator</Th>
                <Th>Industry</Th>
                <Th>Path</Th>
                <Th>Maturity</Th>
                <Th>Register</Th>
                <Th>Top priority</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-rule hover:bg-cream-dark">
                  <td className="px-4 py-3 text-ink-light font-mono text-[12px]" title={item.delivered_at || ''}>
                    {item.delivered_at ? new Date(item.delivered_at).toISOString().slice(0, 10) : '—'}
                  </td>
                  <td className="px-4 py-3 text-ink">
                    {item.first_name || <span className="italic text-ink-light">—</span>}
                    {item.version === 'v1' && <span className="font-mono text-[10px] uppercase ml-2 text-ink-light bg-cream-dark px-1.5 py-0.5">v1</span>}
                    {item.demo_mode && <span className="font-mono text-[10px] uppercase ml-2 text-gold-warm bg-cream-dark px-1.5 py-0.5">demo</span>}
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{industryShort(item.industry)}</td>
                  <td className="px-4 py-3 text-ink-soft font-mono text-[12px]">{item.path === 'pathA' ? 'FL' : item.path === 'pathB' ? 'ML' : '—'}</td>
                  <td className="px-4 py-3 text-ink-soft font-mono text-[12px]">{item.maturity_score != null ? `${item.maturity_score}/10` : '—'}</td>
                  <td className="px-4 py-3 text-ink-soft text-xs">{item.register || '—'}</td>
                  <td className="px-4 py-3 text-ink-soft text-xs max-w-xs truncate" title={item.top_priority || ''}>{item.top_priority || '—'}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {item.pdf_url && (
                      <button
                        type="button"
                        onClick={() => handleDownload(item)}
                        disabled={busyId === item.id}
                        className="font-mono text-[11px] uppercase tracking-wider text-accent hover:underline disabled:opacity-50 mr-3"
                        title="Download the synthesis PDF for this delivery."
                      >
                        {busyId === item.id ? 'Loading…' : 'PDF ↓'}
                      </button>
                    )}
                    {!item.pdf_url && item.pdf_error && (
                      <button
                        type="button"
                        onClick={() => handleRerender(item)}
                        disabled={busyId === item.id}
                        className="font-mono text-[11px] uppercase tracking-wider text-accent hover:underline disabled:opacity-50 mr-3"
                        title={`PDF generation failed: ${item.pdf_error}. Click to retry.`}
                      >
                        {busyId === item.id ? 'Rendering…' : 'Re-render PDF'}
                      </button>
                    )}
                    <Link to={`/review/${item.id}`} className="font-mono text-[11px] uppercase tracking-wider text-accent hover:underline">View →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-light">{children}</th>;
}

function Sel({ label, value, options, onChange }: { label: string; value: string; options: { value: string; label: string }[]; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col">
      <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-paper border border-rule px-3 py-1.5 font-serif text-sm text-ink focus:outline-none focus:border-accent">
        {options.map((o) => <option key={o.value || 'none'} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function industryShort(key: string | null): string {
  if (!key) return '—';
  const map: Record<string, string> = {
    professional_services: 'professional services',
    real_estate: 'real estate',
    health_wellness: 'health & wellness',
    ecommerce_retail: 'e-commerce',
    trades_manufacturing: 'trades',
    saas_software: 'SaaS',
    other: 'other',
  };
  return map[key] || key;
}
